import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, PencilSimple, Copy, Printer, DownloadSimple,
  ArrowsClockwise, ShieldCheck,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import StatusPill from "@/components/StatusPill";
import { formatIdDateTime } from "@/lib/format";

/**
 * Top block of the SPB detail page:
 *   • "Back to list" link (hidden for petugas — they only see one SPB at a time)
 *   • Title + StatusPill
 *   • Cluster of action buttons: Edit, Clone, Preview A5, Cetak A4, Cetak Ulang QR, Download
 *   • Two info banners (green reprint hint / amber legacy hint)
 *   • Verification highlight banner
 *
 * Split from `SPBDetail.jsx` Feb 2026 to keep the orchestrator under 700 lines.
 * All state stays in the parent — this is a presentational component that just
 * renders JSX and forwards click handlers.
 */
export default function SPBHeader({
  spb, id, user, pdfHref,
  reprintBusy, onReprintQR,
}) {
  return (
    <>
      {user?.role !== "petugas" && (
        <Link to="/spb" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4 no-print">
          <ArrowLeft size={14} /> Kembali ke Daftar SPB
        </Link>
      )}

      <div className="border-b border-border pb-6 mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4 no-print">
        <div>
          <div className="overline">Detail Surat</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 font-mono">SPB · {spb.no_spb}</h1>
          <div className="mt-3"><StatusPill status={spb.status} /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          {spb.status === "belum_termuat" && (
            <Link to={`/spb/${id}/edit`}>
              <Button variant="outline" className="rounded-lg" data-testid="edit-spb-button">
                <PencilSimple size={16} className="mr-2" /> Edit SPB
              </Button>
            </Link>
          )}
          {(user?.role === "admin" || user?.role === "admin_kantor") && (
            <Link to={`/spb/new?clone=${id}`}>
              <Button variant="outline" className="rounded-lg" data-testid="clone-spb-button" title="Buat SPB baru berdasarkan SPB ini">
                <Copy size={16} className="mr-2" /> Clone
              </Button>
            </Link>
          )}
          <a href={pdfHref} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-lg" data-testid="preview-pdf-button">
              <Printer size={16} className="mr-2" /> Preview / Cetak (A5)
            </Button>
          </a>
          <a href={`${pdfHref}${pdfHref.includes("?") ? "&" : "?"}paper=A4`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-lg" data-testid="preview-pdf-a4-button">
              <Printer size={16} className="mr-2" /> Cetak A4
            </Button>
          </a>
          <Button
            type="button"
            variant="outline"
            onClick={onReprintQR}
            disabled={reprintBusy}
            className="rounded-lg border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            data-testid="reprint-qr-button"
            title="Kalau SPB ini dibuat sebelum fitur QR ada, tombol ini akan otomatis menambahkan QR PEMBUAT SPB, lalu buka PDF"
          >
            <ArrowsClockwise size={16} className={`mr-2 ${reprintBusy ? "animate-spin" : ""}`} />
            {reprintBusy ? "Menyiapkan…" : "Cetak Ulang QR"}
          </Button>
          <a href={pdfHref} download={`SPB-${spb.no_spb}.pdf`}>
            <Button className="rounded-lg font-bold uppercase tracking-wider" data-testid="download-pdf-button">
              <DownloadSimple size={16} className="mr-2" /> Download PDF
            </Button>
          </a>
        </div>
      </div>

      {/* Info banner — reprint hint (green) OR legacy no-QR notice (amber) */}
      {spb.qr_signatures?.length > 0 ? (
        <div className="mb-6 border border-emerald-400 bg-emerald-50 p-3 text-xs flex items-start gap-2 no-print"
             data-testid="reprint-qr-notice">
          <ArrowsClockwise size={16} weight="bold" className="text-emerald-700 mt-0.5 shrink-0" />
          <div>
            <span className="font-bold text-emerald-800">QR di kotak DIBUAT/DITERIMA</span>{" "}
            <span className="text-emerald-800">sudah pakai URL pendek yang lebih mudah discan HP.</span>{" "}
            Kalau kamu punya print-out lama (pre-Feb 2026) yang QR-nya susah discan, klik{" "}
            <strong>&quot;Cetak Ulang QR&quot;</strong> di atas untuk PDF versi baru — data tanda tangan tetap sama.
          </div>
        </div>
      ) : (
        <div className="mb-6 border border-amber-400 bg-amber-50 p-3 text-xs flex items-start gap-2 no-print"
             data-testid="reprint-qr-legacy-notice">
          <ArrowsClockwise size={16} weight="bold" className="text-amber-700 mt-0.5 shrink-0" />
          <div>
            <span className="font-bold text-amber-900">SPB ini belum punya QR tanda tangan.</span>{" "}
            <span className="text-amber-900">
              Klik <strong>&quot;Cetak Ulang QR&quot;</strong> untuk otomatis menambahkan QR PEMBUAT SPB
              pakai akun kamu, lalu PDF terbuka dengan QR di kotak <em>DIBUAT OLEH</em>.
              Untuk QR di kotak <em>DITERIMA OLEH</em>, minta supir/pelanggan scan &amp; sign lewat panel di bawah.
            </span>
          </div>
        </div>
      )}

      {/* Verification highlight — only for diverifikasi status */}
      {spb.status === "diverifikasi" && (spb.diverifikasi_by || spb.diverifikasi_at) && (
        <div className="mb-6 border-2 border-emerald-500 bg-emerald-50 p-4 flex items-start gap-3 no-print"
             data-testid="verified-banner">
          <div className="w-10 h-10 flex-shrink-0 bg-emerald-500 text-white flex items-center justify-center">
            <ShieldCheck size={22} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="overline text-emerald-700">Sudah Terverifikasi</div>
            <div className="text-sm font-bold text-emerald-900 mt-0.5">
              Diverifikasi oleh{" "}
              <span className="font-mono">
                {spb.diverifikasi_by_name || spb.diverifikasi_by || "Petugas Loading"}
              </span>
              {spb.diverifikasi_by && spb.diverifikasi_by_name && (
                <span className="text-emerald-700 font-normal ml-1">({spb.diverifikasi_by})</span>
              )}
            </div>
            {spb.diverifikasi_at && (
              <div className="text-xs text-emerald-700 mt-0.5 font-mono">
                pada {formatIdDateTime(spb.diverifikasi_at)}
              </div>
            )}
            <p className="text-[11px] text-emerald-700 mt-2 italic">
              SPB telah diverifikasi secara fisik di lapangan oleh Petugas Loading.
              Data ini terkunci dari perubahan status maupun penghapusan.
            </p>
          </div>
        </div>
      )}

      <div className="mb-4 text-xs text-muted-foreground no-print" data-testid="print-hint">
        Format default <strong>A5 landscape</strong>. Pada dialog cetak browser, aktifkan opsi{" "}
        <em>&quot;Fit to printable area&quot;</em> / <em>&quot;Scale to fit&quot;</em>{" "}
        jika printer Anda menggunakan kertas A4/Letter.
      </div>
    </>
  );
}
